import React, { useState, useEffect, JSX } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  useMediaQuery,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  PhotoCamera,
  Edit,
  Save,
  Cancel,
  Add,
  Delete,
  Work,
  School,
  EmojiEvents,
  LocationOn,
  Phone,
  Email,
  AccountCircle,
  LiveHelp,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from 'hooks/AuthContext';
import * as Types from 'types/types';
import { useAppState } from 'hooks/GlobalContext';
import { BackstoryQuery } from 'components/BackstoryQuery';
import { TransientChat } from 'components/ui/TransientChat';
import { ConversationHandle } from 'components/Conversation';

// Styled components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface TabPanelProps {
  children?: React.ReactNode;
  value: string;
  active: string;
}

function TabPanel(props: TabPanelProps): JSX.Element {
  const { children, value, active, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={active !== value}
      id={`profile-tabpanel-${value}`}
      aria-labelledby={`profile-tab-${value}`}
      {...other}
    >
      {value === active && (
        <Box
          sx={{
            p: { xs: 1, sm: 3 },
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

const CandidateProfile: React.FC = () => {
  const theme = useTheme();
  const { setSnack } = useAppState();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, updateUserData, apiClient } = useAuth();
  const chatRef = React.useRef<ConversationHandle>(null);
  const [dirty, setDirty] = useState(false);

  // Check if user is a candidate
  const candidate = user?.userType === 'candidate' ? (user as Types.Candidate) : null;

  // State management
  const [tabValue, setTabValue] = useState<string>('info');
  const [editMode, setEditMode] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(false);

  // Form data state
  const [formData, setFormData] = useState<Partial<Types.Candidate>>({});
  const [profileImage, setProfileImage] = useState<string>('');

  // Dialog states
  const [skillDialog, setSkillDialog] = useState(false);
  const [experienceDialog, setExperienceDialog] = useState(false);

  // New item states
  const [newSkill, setNewSkill] = useState<Partial<Types.Skill>>({
    name: '',
    category: '',
    level: 'beginner',
    yearsOfExperience: 0,
  });

  const [newQuestion, setNewQuestion] = useState<Partial<Types.CandidateQuestion>>({
    question: '',
  });

  const [newExperience, setNewExperience] = useState<Partial<Types.WorkExperience>>({
    companyName: '',
    position: '',
    startDate: new Date(),
    isCurrent: false,
    description: '',
    skills: [],
    location: { text: '' },
  });

  useEffect(() => {
    if (dirty) {
      handleSave();
      setDirty(false);
    }
  }, [formData, dirty, setDirty]);

  useEffect(() => {
    if (candidate) {
      setFormData(candidate);
      if (candidate.profileImage) {
        setProfileImage(`/api/1.0/candidates/profile/${candidate.username}`);
      } else {
        setProfileImage('');
      }
    }
  }, [candidate]);

  if (!candidate) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Access denied. This page is only available for candidates.</Alert>
      </Container>
    );
  }

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: string): void => {
    setTabValue(newValue);
  };

  // Handle form input changes
  const handleInputChange = (field: string, value: boolean | string | Types.Location): void => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Handle profile image upload
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (!e.target.files || !e.target.files[0]) {
      return;
    }

    try {
      console.log('Uploading profile image:', e.target.files[0]);
      const success = await apiClient.uploadCandidateProfile(e.target.files[0]);
      if (success) {
        setProfileImage(URL.createObjectURL(e.target.files[0]));
        candidate.profileImage = 'profile.' + e.target.files[0].name.replace(/^.*\./, '');
        console.log(`Set profile image to: ${candidate.profileImage}`);
        updateUserData(candidate);
      } else {
        setSnack('Failed to upload profile image. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error uploading profile image:', error);
      setSnack('Failed to upload profile image. Please try again.', 'error');
    }
  };

  // Toggle edit mode for a section
  const toggleEditMode = (section: string): void => {
    setEditMode({
      ...editMode,
      [section]: !editMode[section],
    });
  };

  // Save changes
  const handleSave = async (): Promise<void> => {
    setLoading(true);
    try {
      if (candidate.id) {
        const updatedCandidate = await apiClient.updateCandidate(candidate.id, formData);
        updateUserData(updatedCandidate);
        setSnack('Profile updated successfully!');
      }
    } catch (error) {
      setSnack('Failed to update profile. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Cancel edit
  const handleCancel = (section: string): void => {
    setFormData(candidate);
    toggleEditMode(section);
  };

  // Add new skill
  const handleAddSkill = (): void => {
    if (newSkill.name && newSkill.category) {
      const updatedSkills = [...(formData.skills || []), newSkill as Types.Skill];
      setFormData({ ...formData, skills: updatedSkills });
      setNewSkill({
        name: '',
        category: '',
        level: 'beginner',
        yearsOfExperience: 0,
      });
      setSkillDialog(false);
      setSnack('Skill added successfully!');
      setDirty(true);
    }
  };

  // Remove skill
  const handleRemoveSkill = (index: number): void => {
    const updatedSkills = (formData.skills || []).filter((_, i) => i !== index);
    setFormData({ ...formData, skills: updatedSkills });
    setSnack('Skill removed successfully!');
    setDirty(true);
  };

  // Add new question
  const handleAddQuestion = (): void => {
    if (newQuestion.question?.trim()) {
      const questionToAdd: Types.CandidateQuestion = {
        question: newQuestion.question.trim(),
      };
      const updatedQuestions = [...(formData.questions || []), questionToAdd];
      setFormData({ ...formData, questions: updatedQuestions });
      setNewQuestion({
        question: '',
      });
      setEditMode({ ...editMode, questions: false });
      setSnack('Question added successfully!');
      setDirty(true);
    }
  };

  // Remove question
  const handleRemoveQuestion = (index: number): void => {
    const updatedQuestions = (formData.questions || []).filter((_, i) => i !== index);
    setFormData({ ...formData, questions: updatedQuestions });
    setSnack('Question removed successfully!');
    setDirty(true);
  };

  // Add new work experience
  const handleAddExperience = (): void => {
    if (newExperience.companyName && newExperience.position) {
      const updatedExperience = [
        ...(formData.experience || []),
        newExperience as Types.WorkExperience,
      ];
      setFormData({ ...formData, experience: updatedExperience });
      setNewExperience({
        companyName: '',
        position: '',
        startDate: new Date(),
        isCurrent: false,
        description: '',
        skills: [],
        location: { text: '' },
      });
      setExperienceDialog(false);
      setSnack('Experience added successfully!');
    }
  };

  // Remove work experience
  const handleRemoveExperience = (index: number): void => {
    const updatedExperience = (formData.experience || []).filter((_, i) => i !== index);
    setFormData({ ...formData, experience: updatedExperience });
    setSnack('Experience removed successfully!');
    setDirty(true);
  };

  // Basic Information Tab
  const renderBasicInfo = (): JSX.Element => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        '& .entry': {
          flexDirection: 'column',
          fontSize: '0.9rem',
          display: 'flex',
          mt: 1,
        },
        '& .title': { display: 'flex', fontWeight: 'bold' },
      }}
    >
      <Box sx={{ textAlign: 'center', mb: { xs: 1, sm: 2 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar
            src={profileImage}
            sx={{
              width: { xs: 80, sm: 120 },
              height: { xs: 80, sm: 120 },
              mb: { xs: 1, sm: 2 },
              border: `2px solid ${theme.palette.primary.main}`,
            }}
          >
            {!profileImage && <AccountCircle sx={{ fontSize: { xs: 50, sm: 80 } }} />}
          </Avatar>
          {editMode.basic && (
            <>
              <IconButton
                color="primary"
                aria-label="upload picture"
                component="label"
                size={isMobile ? 'small' : 'medium'}
              >
                <PhotoCamera />
                <VisuallyHiddenInput type="file" accept="image/*" onChange={handleImageUpload} />
              </IconButton>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{
                  textAlign: 'center',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                }}
              >
                Update profile photo
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Box
        className="entry"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          gap: 1,
          '& span': { mb: 0 },
        }}
      >
        {editMode.basic ? (
          <FormControlLabel
            sx={{ display: 'flex' }}
            control={
              <Switch
                checked={formData.isPublic}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                  handleInputChange('isPublic', event.target.checked)
                }
              />
            }
            label={
              formData.isPublic
                ? 'Your account will appear in candidate lists and searches on Backstory.'
                : `Your account will not be listed as a candidate. You can still share your profile by users navigating to '/u/${candidate.username}'.`
            }
          />
        ) : (
          <>
            {candidate.isPublic
              ? 'Your account will appear in candidate lists and searches on Backstory.'
              : `Your account will not be listed as a candidate. You can still share your profile by users navigating to '/u/${candidate.username}'.`}
          </>
        )}
      </Box>

      <Box className="entry">
        {editMode.basic ? (
          <TextField
            fullWidth
            label="First Name"
            value={formData.firstName || ''}
            onChange={(e): void => handleInputChange('firstName', e.target.value)}
            variant="outlined"
          />
        ) : (
          <>
            <Box className="title">First Name</Box>
            <Box className="value">{candidate.firstName}</Box>
          </>
        )}
      </Box>

      <Box className="entry">
        {editMode.basic ? (
          <TextField
            fullWidth
            label="Last Name"
            value={formData.lastName || ''}
            onChange={(e): void => handleInputChange('lastName', e.target.value)}
            variant="outlined"
          />
        ) : (
          <>
            <Box className="title">Last Name</Box>
            <Box className="value">{candidate.lastName}</Box>
          </>
        )}
      </Box>

      <Box className="entry">
        <Box className="title">
          <Email sx={{ mr: 1, verticalAlign: 'middle' }} />
          Email
        </Box>
        <Box className="value">{candidate.email}</Box>
      </Box>

      <Box className="entry">
        {editMode.basic ? (
          <TextField
            fullWidth
            label="Phone"
            value={formData.phone || ''}
            onChange={(e): void => handleInputChange('phone', e.target.value)}
            variant="outlined"
          />
        ) : (
          <>
            <Box className="title">
              <Phone sx={{ mr: 1, verticalAlign: 'middle' }} />
              Phone
            </Box>
            <Box className="value">{candidate.phone || 'Not provided'}</Box>
          </>
        )}
      </Box>

      <Box className="entry">
        {editMode.basic ? (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Professional Summary"
            value={formData.description || ''}
            onChange={(e): void => handleInputChange('description', e.target.value)}
            variant="outlined"
          />
        ) : (
          <>
            <Box className="title">Professional Summary</Box>
            <Box className="value">{candidate.description || 'No summary provided'}</Box>
          </>
        )}
      </Box>

      <Box className="entry">
        {editMode.basic ? (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Location"
            value={formData.location?.text || ''}
            onChange={(e): void => {
              setFormData({
                ...formData,
                location: { ...formData.location, text: e.target.value },
              });
            }}
            variant="outlined"
          />
        ) : (
          <>
            <Box className="title">
              <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
              Location
            </Box>
            <Box className="value">
              {candidate.location?.city
                ? `${candidate.location?.city} ${candidate.location?.country}`
                : candidate.location?.text || 'Not provided'}
            </Box>
          </>
        )}
      </Box>

      <Box className="entry">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'flex-end',
            gap: 2,
            mt: { xs: 2, sm: 0 },
          }}
        >
          {editMode.basic ? (
            <>
              <Button
                variant="outlined"
                onClick={(): void => handleCancel('basic')}
                startIcon={<Cancel />}
                fullWidth={isMobile}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={(): void => {
                  handleSave();
                }}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                disabled={loading}
                fullWidth={isMobile}
              >
                Save
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              onClick={(): void => {
                toggleEditMode('basic');
              }}
              startIcon={<Edit />}
              fullWidth={isMobile}
            >
              Edit Info
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );

  // Skills Tab
  const renderSkills = (): JSX.Element => (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: { xs: 2, sm: 3 },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Skills & Expertise</Typography>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={(): void => setSkillDialog(true)}
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
        >
          Add Skill
        </Button>
      </Box>

      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ maxWidth: '100%' }}>
        {(formData.skills || []).map((skill, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant={isMobile ? 'subtitle2' : 'h6'}
                      component="div"
                      sx={{
                        fontSize: { xs: '0.9rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                      }}
                    >
                      {skill.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        wordBreak: 'break-word',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      {skill.category}
                    </Typography>
                    <Chip
                      size="small"
                      label={skill.level}
                      color="primary"
                      variant="outlined"
                      sx={{
                        mt: 1,
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        height: { xs: 20, sm: 24 },
                      }}
                    />
                    {skill.yearsOfExperience && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      >
                        {skill.yearsOfExperience} years experience
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(): void => handleRemoveSkill(index)}
                    color="error"
                    sx={{ ml: 1 }}
                  >
                    <Delete sx={{ fontSize: { xs: 16, sm: 20 } }} />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {(!formData.skills || formData.skills.length === 0) && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No skills added yet. Click &quot;Add Skill&quot; to get started.
        </Typography>
      )}
    </Box>
  );

  const handleSubmitQuestion = (question: Types.CandidateQuestion): void => {
    console.log('Submitting question:', question);
    const query: Types.ChatQuery = {
      prompt: question.question,
    };
    chatRef.current?.submitQuery(query);
  };

  // Questions Tab
  const renderQuestions = (): JSX.Element => (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: { xs: 2, sm: 3 },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Questions for Chat</Typography>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={(): void => {
            setEditMode({ ...editMode, questions: true });
          }}
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
        >
          Add Question
        </Button>
      </Box>

      <Box>See how Backstory answers the question about you...</Box>
      <TransientChat id={Date()} ref={chatRef} />

      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ maxWidth: '100%' }}>
        {(formData.questions || []).map((question, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <BackstoryQuery question={question} submitQuery={handleSubmitQuestion} />
                    {/* Display question text
                    <Typography
                      variant={isMobile ? 'subtitle2' : 'h6'}
                      component="div"
                      sx={{
                        fontSize: { xs: '0.9rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        mb: 1,
                      }}
                    >
                      {question.question}
                    </Typography> */}
                    {/* {question.category && (
                      <Chip
                        size="small"
                        label={question.category}
                        color="secondary"
                        variant="outlined"
                        sx={{
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          height: { xs: 20, sm: 24 },
                        }}
                      />
                    )} */}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(): void => handleRemoveQuestion(index)}
                    color="error"
                    sx={{ ml: 1 }}
                  >
                    <Delete sx={{ fontSize: { xs: 16, sm: 20 } }} />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {(!formData.questions || formData.questions.length === 0) && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No questions added yet. Click &quot;Add Question&quot; to get started.
        </Typography>
      )}
    </Box>
  );

  // Experience Tab
  const renderExperience = (): JSX.Element => (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: { xs: 2, sm: 3 },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Work Experience</Typography>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={(): void => setExperienceDialog(true)}
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
        >
          Add Experience
        </Button>
      </Box>

      {(formData.experience || []).map((exp, index) => (
        <Card key={index} sx={{ mb: { xs: 1.5, sm: 2 }, overflow: 'hidden' }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: { xs: 1, sm: 0 },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant={isMobile ? 'subtitle1' : 'h6'}
                  component="div"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    wordBreak: 'break-word',
                  }}
                >
                  {exp.position}
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="primary"
                  sx={{
                    wordBreak: 'break-word',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                  }}
                >
                  {exp.companyName}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  {exp.startDate?.toLocaleDateString()} -{' '}
                  {exp.isCurrent ? 'Present' : exp.endDate?.toLocaleDateString()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    wordBreak: 'break-word',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  }}
                >
                  {exp.description}
                </Typography>
                {exp.skills && exp.skills.length > 0 && (
                  <Box sx={{ mt: { xs: 1, sm: 2 } }}>
                    {exp.skills.map((skill, skillIndex) => (
                      <Chip
                        key={skillIndex}
                        label={skill}
                        size="small"
                        sx={{
                          mr: 0.5,
                          mb: 0.5,
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          height: { xs: 20, sm: 24 },
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
              <IconButton
                onClick={(): void => {
                  handleRemoveExperience(index);
                }}
                color="error"
                size="small"
                sx={{
                  alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                  ml: { sm: 1 },
                }}
              >
                <Delete sx={{ fontSize: { xs: 16, sm: 20 } }} />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      ))}

      {(!formData.experience || formData.experience.length === 0) && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            py: { xs: 2, sm: 4 },
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
          }}
        >
          No work experience added yet. Click &quot;Add Experience&quot; to get started.
        </Typography>
      )}
    </Box>
  );

  const renderEducation = (): JSX.Element => (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: { xs: 2, sm: 3 },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Education</Typography>
        <Button
          variant="outlined"
          startIcon={<Add />}
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
        >
          Add Education
        </Button>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          textAlign: 'center',
          py: { xs: 2, sm: 4 },
          fontSize: { xs: '0.8rem', sm: '0.875rem' },
        }}
      >
        No education added yet. Click &quot;Add Education&quot; to get started.
      </Typography>
    </Box>
  );

  return (
    <Container
      maxWidth="lg"
      sx={{
        mt: { xs: 1, sm: 4 },
        mb: { xs: 1, sm: 4 },
        px: { xs: 0.5, sm: 3 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          overflow: 'hidden',
          mx: { xs: 0, sm: 0 },
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-flexContainer': {
                justifyContent: isMobile ? 'flex-start' : 'center',
              },
              '& .MuiTab-root': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minWidth: { xs: 60, sm: 120 },
                padding: { xs: '6px 8px', sm: '12px 16px' },
              },
            }}
          >
            <Tab
              label={isMobile ? 'Info' : 'Basic Info'}
              value="info"
              icon={<AccountCircle sx={{ fontSize: { xs: 18, sm: 24 } }} />}
              iconPosition={isMobile ? 'top' : 'start'}
            />
            <Tab
              label="Questions"
              value="questions"
              icon={<LiveHelp sx={{ fontSize: { xs: 18, sm: 24 } }} />}
              iconPosition={isMobile ? 'top' : 'start'}
            />
            <Tab
              label="Skills"
              value="skills"
              icon={<EmojiEvents sx={{ fontSize: { xs: 18, sm: 24 } }} />}
              iconPosition={isMobile ? 'top' : 'start'}
            />
            <Tab
              label={isMobile ? 'Work' : 'Experience'}
              value="experience"
              icon={<Work sx={{ fontSize: { xs: 18, sm: 24 } }} />}
              iconPosition={isMobile ? 'top' : 'start'}
            />
            <Tab
              label={isMobile ? 'Edu' : 'Education'}
              value="education"
              icon={<School sx={{ fontSize: { xs: 18, sm: 24 } }} />}
              iconPosition={isMobile ? 'top' : 'start'}
            />
          </Tabs>
        </Box>

        <TabPanel value="info" active={tabValue}>
          {renderBasicInfo()}
        </TabPanel>

        <TabPanel value="questions" active={tabValue}>
          {renderQuestions()}
        </TabPanel>

        <TabPanel value="skills" active={tabValue}>
          {renderSkills()}
        </TabPanel>

        <TabPanel value="experience" active={tabValue}>
          {renderExperience()}
        </TabPanel>

        <TabPanel value="education" active={tabValue}>
          {renderEducation()}
        </TabPanel>
      </Paper>

      {/* Add Skill Dialog */}
      <Dialog
        open={skillDialog}
        onClose={(): void => setSkillDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: '100%',
              height: '100%',
              maxHeight: '100%',
            }),
          },
        }}
      >
        <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>Add New Skill</DialogTitle>
        <DialogContent
          sx={{
            overflow: 'auto',
            pt: { xs: 1, sm: 2 },
          }}
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 0.5, maxWidth: '100%' }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Skill Name"
                value={newSkill.name || ''}
                onChange={(e): void => setNewSkill({ ...newSkill, name: e.target.value })}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Category"
                value={newSkill.category || ''}
                onChange={(e): void => setNewSkill({ ...newSkill, category: e.target.value })}
                placeholder="e.g., Programming, Design, Marketing"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Proficiency Level</InputLabel>
                <Select
                  value={newSkill.level || 'beginner'}
                  onChange={(e): void =>
                    setNewSkill({
                      ...newSkill,
                      level: e.target.value as 'beginner' | 'intermediate' | 'advanced' | 'expert',
                    })
                  }
                  label="Proficiency Level"
                >
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                  <MenuItem value="expert">Expert</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Years of Experience"
                value={newSkill.yearsOfExperience || 0}
                onChange={(e): void =>
                  setNewSkill({
                    ...newSkill,
                    yearsOfExperience: parseInt(e.target.value) || 0,
                  })
                }
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            p: { xs: 1.5, sm: 3 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button
            onClick={(): void => setSkillDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddSkill}
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            Add Skill
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Question Dialog */}
      <Dialog
        open={editMode['questions']}
        onClose={(): void => setEditMode({ ...editMode, questions: false })}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: '100%',
              height: '100%',
              maxHeight: '100%',
            }),
          },
        }}
      >
        <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>Add New Question</DialogTitle>
        <DialogContent
          sx={{
            overflow: 'auto',
            pt: { xs: 1, sm: 2 },
          }}
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 0.5, maxWidth: '100%' }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 3 : 4}
                label="Question"
                value={newQuestion.question || ''}
                onChange={(e): void => setNewQuestion({ ...newQuestion, question: e.target.value })}
                placeholder="What would should potential employers ask about you? For example, 'What are your strengths?'"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            {/* <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label="Category (Optional)"
                value={newQuestion.category || ''}
                onChange={(e): void => setNewQuestion({ ...newQuestion, category: e.target.value })}
                placeholder="e.g., General, Technical, Behavioral"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid> */}
            {/* <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newQuestion.isActive !== false}
                    onChange={(e): void =>
                      setNewQuestion({
                        ...newQuestion,
                        isActive: e.target.checked,
                      })
                    }
                    size={isMobile ? 'small' : 'medium'}
                  />
                }
                label="Active"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid> */}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            p: { xs: 1.5, sm: 3 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button
            onClick={(): void => {
              setEditMode({ ...editMode, questions: false });
            }}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddQuestion}
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
            disabled={!newQuestion.question?.trim()}
          >
            Add Question
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Experience Dialog */}
      <Dialog
        open={experienceDialog}
        onClose={(): void => setExperienceDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: '100%',
              height: '100%',
              maxHeight: '100%',
            }),
          },
        }}
      >
        <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>Add Work Experience</DialogTitle>
        <DialogContent
          sx={{
            overflow: 'auto',
            pt: { xs: 1, sm: 2 },
          }}
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 0.5, maxWidth: '100%' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Company Name"
                value={newExperience.companyName || ''}
                onChange={(e): void =>
                  setNewExperience({
                    ...newExperience,
                    companyName: e.target.value,
                  })
                }
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Position/Title"
                value={newExperience.position || ''}
                onChange={(e): void =>
                  setNewExperience({
                    ...newExperience,
                    position: e.target.value,
                  })
                }
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={newExperience.startDate?.toISOString().split('T')[0] || ''}
                onChange={(e): void =>
                  setNewExperience({
                    ...newExperience,
                    startDate: new Date(e.target.value),
                  })
                }
                InputLabelProps={{ shrink: true }}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newExperience.isCurrent || false}
                    onChange={(e): void =>
                      setNewExperience({
                        ...newExperience,
                        isCurrent: e.target.checked,
                      })
                    }
                    size={isMobile ? 'small' : 'medium'}
                  />
                }
                label="Currently working here"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 3 : 4}
                label="Job Description"
                value={newExperience.description || ''}
                onChange={(e): void =>
                  setNewExperience({
                    ...newExperience,
                    description: e.target.value,
                  })
                }
                placeholder="Describe your responsibilities and achievements..."
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            p: { xs: 1.5, sm: 3 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button
            onClick={(): void => setExperienceDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddExperience}
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            Add Experience
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export { CandidateProfile };
